/* eslint-disable no-console */
import * as Sentry from '@sentry/vue';
import { FetchError } from 'ofetch';

/**
 * Handles App errors.
 * All errors are passed to this function.
 *
 * 1. It shows visible error toast to user (if desired)
 * 2. It removes loading overlay
 * 3. It logs the error to console (in dev mode)
 * 4. It logs the error to Sentry
 *
 * @param error any kind of error
 * @param toastType What kind of toast to show. false = no toast
 * @param message What kind of message to show. 'generic' = generic message, 'original' = error.message, string = custom message
 * @param context Additional context to log with the error
 */
export function handleError(
    error: any,
    toastType: false | 'error' | 'info' = 'error',
    message: 'generic' | 'original' | string = 'generic',
    context: Record<string, unknown> = {},
) {
    let genericMessage = 'core.errorMessages.500';
    let serializedContext = {};
    try {
        serializedContext = JSON.parse(JSON.stringify(context));
    }
    catch (error) {

    }

    // Handle TOT API errors differently
    if (error instanceof FetchError || error?.cause instanceof FetchError) {
        genericMessage = 'core.errorMessages.apiGeneric';
        handleAPIError(error, serializedContext);
    }
    else {
        handleGenericError(error, serializedContext);
    }

    // Make sure loading overlay is hidden
    const { isLoading } = useLoading();
    isLoading.value = false;

    // Show toast messages
    const $t = useNuxtApp()?.$i18n?.t ?? ((key: string) => key);
    const { addToast } = useToasts();

    let toastText = message;
    if (message === 'generic') {
        toastText = $t(genericMessage);
    }
    if (message === 'original') {
        toastText = error?.message ?? '';
    }
    if (toastType) {
        addToast(toastType, toastType, toastText, 0, true);
    }
}

/**
 * Handles API errors by logging them to the console and Sentry.
 * Logging important API request infos
 *
 * @param error - The API error to handle.
 * @param context - Additional context information for the error.
 */
function handleAPIError(
    error: Error,
    context: Record<string, unknown> = {},
) {
    const fetchError = error instanceof FetchError ? error as FetchError : error?.cause as FetchError;
    const apiTraceId = fetchError?.response?.headers?.get('x-traceid');
    error.message = `API Error: ${error?.message} (${fetchError?.status})`;

    if (import.meta.dev) {
        console.error(error);
        console.debug('Details:', fetchError?.data?.detail);
        console.debug('Status:', fetchError?.status);
        console.debug('API Trace ID:', apiTraceId);
        console.debug('Detail Context:', context);
    }

    try {
        // Log to Sentry with extra API infos
        Sentry.withScope((scope) => {
            scope.setContext('API Error Details', {
                'Endpoint': fetchError?.data?.instance,
                'Type': fetchError?.data?.type,
                'Title': fetchError?.data?.title,
                'Details': fetchError?.data?.detail,
                'Status': fetchError?.status,
                'Toft API Trace Id': apiTraceId,
            });
            scope.setContext('Detail Context', context);
            scope.setTag('toft_api', true);
            scope.setTag('toft_err_status', fetchError?.status);
            scope.setTag('toft_err_type', fetchError?.data?.type);
            scope.setTag('locale', useNuxtApp()?.$i18n?.locale.value);

            Sentry.captureException(error);
        });
    }
    catch (error) {
        console.error('Error while logging to Sentry', error);
    }
}

/**
 * Handles API errors by logging them to the console and Sentry.
 * Logging important API request infos
 *
 * @param error - The API error to handle.
 * @param context - Additional context information for the error.
 */
function handleGenericError(
    error: any,
    context: Record<string, unknown> = {},
) {
    if (error?.status === 404) {
        return;
    }

    if (import.meta.dev) {
        console.error(error);
        console.debug('Detail Context', context);
    }

    try {
        Sentry.withScope((scope) => {
            scope.setContext('Detail Context', context);
            scope.setTag('locale', useNuxtApp()?.$i18n?.locale.value);
            Sentry.captureException(error);
        });
    }
    catch (error) {
        console.error('Error while logging to Sentry', error);
    }
}

/**
 * Handles Datatrans payment errors.
 * This includes regular failing payments and 3D secure errors.
 */
export function handleDatatransPaymentError(error: Error) {
    const { setErrorToast } = useToasts();
    const { t: $t } = useNuxtApp().$i18n;
    setErrorToast($t('core.errorMessages.paymentGeneric'));

    if (import.meta.dev) {
        console.error('Payment Datatrans Error', error);
    }
}

/**
 * Logs Breadcrumb info to sentry for better understanding what happens
 */
export function logSentryBreadcrumb(message: string, data: Record<string, unknown> = {}) {
    Sentry.addBreadcrumb({
        category: 'App',
        type: 'info',
        message,
        data,
    });
}

/**
 * Allows to log an error to sentry without
 * all the default error handling (like toasts, stopping loading overlays, etc).
 */
export function logSilentSentryError(error: any, context: Record<string, unknown> = {}) {
    console.error('Silent Error:', error);

    try {
        Sentry.withScope((scope) => {
            scope.setContext('Detail Context', context);
            Sentry.captureException(error);
        });
    }
    catch (error) {
        console.error('Error while logging to Sentry', error);
    }
}
